import { supabase } from '../lib/supabase'
import type { StudioRoom, StudioStyle, StudioPlacement } from '../config/blissDesignStudio'

async function request<T>(action: string, body?: Record<string, unknown>, id?: string): Promise<T> {
  const { data } = await supabase.auth.getSession()
  const url = new URL('/api/bliss-design-studio', window.location.origin)
  if (!body) {
    url.searchParams.set('action', action)
    if (id) url.searchParams.set('id', id)
  }
  const response = await fetch(url, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(data.session ? { Authorization: `Bearer ${data.session.access_token}` } : {}),
    },
    body: body ? JSON.stringify({ action, ...body }) : undefined,
  })
  const result = await response.json().catch(() => null)
  if (!response.ok || !result)
    throw new Error(result?.error || 'The studio could not connect. Please try again.')
  return result as T
}

export const studioService = {
  status: () => request<{ available: boolean; accountsAvailable: boolean }>('status'),
  room: (id: string) => request<{ room: StudioRoom }>('room', undefined, id),
  look: (id: string) => request<{ room: StudioRoom }>('look', undefined, id),
  portfolio: () => request<{ rooms: StudioRoom[] }>('portfolio'),
  createRoom: (photo: string, title: string, placement: StudioPlacement) =>
    request<{ room: StudioRoom }>('room', { photo, title, placement }),
  generate: (roomId: string, style: StudioStyle, idea: string, requestId: string) =>
    request<{ room: StudioRoom }>('generate', { roomId, style, idea, requestId }),
  createInspireRoom: (photo: string, modelId: string) =>
    request<{ room: StudioRoom }>('room', { photo, title: 'My inspired room', placement: 'back', mode: 'inspire', modelId }),
  inspire: (roomId: string, modelId: string, idea: string, requestId: string) =>
    request<{ room: StudioRoom }>('generate', { roomId, modelId, idea, requestId, mode: 'inspire', style: 'pearl' }),
  save: (lookId: string, favorite = true) =>
    request<{ room: StudioRoom }>('save', { lookId, favorite }),
  share: (lookId: string) => request<{ token: string; expires_at: string }>('share', { lookId }),
  unshare: (lookId: string) => request<{ revoked: boolean }>('unshare', { lookId }),
}

export async function fetchSharedStudioLook(
  token: string
): Promise<{ title: string; style: string; imageUrl: string; expiresAt: string }> {
  // No Supabase session or bearer token is required. Keep same-origin cookies
  // so hosting access controls can admit viewers of a protected preview.
  const response = await fetch(
    `/api/bliss-design-studio?action=shared&id=${encodeURIComponent(token)}`,
    { credentials: 'same-origin' }
  )
  const result = await response.json()
  if (!response.ok) throw new Error(result.error || 'This shared look is no longer available.')
  return result
}

/** Resize locally before upload; never embed large photos in browser storage or URLs. */
export async function prepareStudioPhoto(file: File): Promise<string> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 15_000_000) {
    throw new Error(
      'Choose a JPG, PNG, or WebP photo under 15 MB. On iPhone, use Take a photo or export a JPG.'
    )
  }
  const url = URL.createObjectURL(file)
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('That photo could not be opened. Please try another.'))
      img.src = url
    })
    const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(image.naturalWidth * scale)
    canvas.height = Math.round(image.naturalHeight * scale)
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Your browser could not prepare this photo.')
    context.fillStyle = '#f8f6ef'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/jpeg', 0.85)
  } finally {
    URL.revokeObjectURL(url)
  }
}

